import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { RouterModule } from '@angular/router';
import { InMemoryCacheService, isGoogleAuthorizationError, MessageType, UtilsService } from 'src/app/common';
import { ChartWrapperComponent } from 'src/app/components/seo/chart-wrapper/chart-wrapper.component';
import { PositionDistributionChartComponent } from 'src/app/components/seo/position-distribution-chart/position-distribution-chart.component';
import { DateRangeOptions } from '../seo/models/date-range-options';
import { BacklinksHistoryRequest, BacklinksRequest, HistoricalRankOverviewRequest } from '../seo/models/site-search-request';
import { SeoApiService } from '../seo/seo-api.service';
import { OnPageService } from '../seo/on-page/on-page.service';
import { GASummaryTotals, GATableRow } from '../seo/models/ga-summary-interface';
import { MenuItem } from 'primeng/api';
import { CommonModule } from '@angular/common';
import { ChartModule } from 'primeng/chart';
import { SelectButtonModule } from 'primeng/selectbutton';
import { ButtonModule } from 'primeng/button';
import { FormsModule } from '@angular/forms';
import { TableModule } from 'primeng/table';
import { PositiveNegativeChartComponent } from 'src/app/components/seo/positive-negative-chart/positive-negative-chart.component';
import { TabMenuModule } from 'primeng/tabmenu';
import { OverviewBoxComponent } from 'src/app/components/overview-box/overview-box.component';
import { DynamicInfoListComponent } from 'src/app/components/seo/dynamic-info-list/dynamic-info-list.component';
import { WebsiteInfoComponent } from 'src/app/components/seo/website-info/website-info.component';
import { GoogleAnalyticsTrafficReportComponent } from '../seo/google-analytics-traffic-report/google-analytics-traffic-report.component';
import { OpportunitiesWorkspaceComponent } from '../opportunities/opportunities-workspace/opportunities-workspace.component';
import { GaSummaryTableComponent } from '../settings/components/3rd-party-integration/ga-summary-table/ga-summary-table.component';
import { SeoLimitsResolver } from '../seo/seo-limit-resolver';
import { ProjectResolver } from 'src/app/common/resolver/project-resolver';
import { ProjectHeaderComponent } from 'src/app/components/project-header/project-header.component';
import { SeoComprehensivePdfExportService } from '../seo/comprehensive-pdf/seo-comprehensive-pdf-export.service';
import { SeoDashboardRequest, SeoDashboardResponse, SeoDashboardSummary, SeoDashboardWidget } from './dashboard-read-model';
import { GscDashboardComponent, GscDashboardState, GscSummary } from './gsc-dashboard/gsc-dashboard.component';

// Pipes
import { TruncateNumberPipe } from 'src/app/common/pipes/truncate.number.pipe';

@Component({
    selector: 'gig-dashboard',
    standalone: true,
    imports:[
        CommonModule,
        SelectButtonModule,
        ButtonModule,
        FormsModule,
        PositiveNegativeChartComponent,
        OverviewBoxComponent,
        GaSummaryTableComponent,
        TabMenuModule,
        GoogleAnalyticsTrafficReportComponent,
        ChartWrapperComponent,
        PositionDistributionChartComponent,
        WebsiteInfoComponent,
        DynamicInfoListComponent,
        ProjectHeaderComponent,
        OpportunitiesWorkspaceComponent,
        GscDashboardComponent,
        RouterModule,

        // Pipes migrated to common module
        TruncateNumberPipe
    ],
    providers: [
        TruncateNumberPipe,
    ],
    templateUrl: './dashboard.component.html',
    styleUrls: ['./dashboard.component.scss']
})
export class DashboardComponent implements OnInit {

    items: MenuItem[] | undefined;
    activeTab: string = 'dashboard';
    activeItem: MenuItem | undefined;
    public trafficData: any;
    public rankedKeywordsData: any;
    public backlinksSummaryData: any;
    public backlinksHistoryData: any;
    public metricType: any
    public metricTypes: any[];

    public rankDoughnut: any;
    public rankDoughnutOptions: any;
    public referringLinkTypeData: any;

    public newBacklinks: any;
    public newReferringDomains: any;

    public lostBacklinks: any;
    public lostReferringDomains: any;

    public brokenBacklinks: any;
    public brokenPages: any;
    public gaSummaryDataActiveUser: any;
    public gaSummaryDataSessions: any;
    public gaSummaryDataNewUser: any;
    public gaSummaryTableData: GATableRow [] = [];
    public gaSummaryTotalArray: GASummaryTotals;
    public gaConnection: boolean = false;
    public gaAuthorizationRequired: boolean = false;
    public isExportingSeoPdf: boolean = false;
    public dashboardGeneratedAt: string | null = null;
    public dashboardWidgets: Record<string, SeoDashboardWidget<any>> = {};
    public dashboardSummary: SeoDashboardSummary | null = null;
    public gscConnected: boolean = false;

    @ViewChild('keywordChartWrapper') keywordChartWrapper: ChartWrapperComponent;
    @ViewChild('trafficChartWrapper') trafficChartWrapper: ChartWrapperComponent;

    @ViewChild('keywordsChart') keywordsChart: PositionDistributionChartComponent;
    @ViewChild('trafficChart') trafficChart: PositionDistributionChartComponent;


    public chartColors1: any;

    public chartColors2: any;
    public backLinksPositiveNegativeChartData: any;
    public backLinksPositiveNegativeChartOptions: any;
    public referringDomainsPositiveNegativeChartData: any;
    public referringDomainsPositiveNegativeChartOptions: any;

    public chartPositionNames: {} = { 'pos_3': 'Top 3', 'pos_4_10': '4-10', 'pos_11_20': '11-20', 'pos_21_50': '21-50', 'pos_51_100': '51-100' };
    public parameters = ['metrics', 'organic'];

    private historicalRankRequestSequence: number = 0;
    private overviewLoadPromise: Promise<boolean> | null = null;

    constructor(
        private cache: InMemoryCacheService,
        private utils: UtilsService,
        private seoApiService: SeoApiService,
        private seoComprehensivePdfExportService: SeoComprehensivePdfExportService,
        private router: Router,
        private route: ActivatedRoute,
        private onPageService : OnPageService
    ) {
        this.utils.breadcrumbs.setItems([{ label: `Dashboard` }, { label: `Domain Overview` }, { label: `${cache?.currentProject?.name ? cache?.currentProject?.name : ''}` }]);
    }

    async ngOnInit(): Promise<void> {

        this.items = [
            {
                label: 'Dashboard',
                tab: 'dashboard',
                icon: 'pi pi-home',
                // command: () => this.activeTab = 'dashboard'
                command: () => this.setActiveTab('dashboard')
            },
            {
                label: 'Domain research',
                tab: 'domain-research',
                icon: 'pi pi-globe',
                command: () => this.setActiveTab('domain-research')
            },
            {
                label: 'Google Analytics KPI',
                tab: 'ga-kpi',
                icon: 'pi pi-globe',
                command: () => this.setActiveTab('ga-kpi')
            },
            // {
            //     label: 'GSC Dashboard',
            //     tab: 'gsc-dashboard',
            //     icon: 'pi pi-search',
            //     command: () => this.setActiveTab('gsc-dashboard')
            // },
            {
                label: 'Opportunities',
                tab: 'opportunities',
                icon: 'pi pi-lightbulb',
                command: () => this.setActiveTab('opportunities')
            }
        ]

        const routeSection: string | undefined = this.route.snapshot.data?.['dashboardSection'];
        const legacyTab: string | null = this.route.snapshot.queryParamMap.get('tab');
        this.setTabState(legacyTab || routeSection || 'dashboard');

        if (legacyTab) {
            await this.navigateToSection(this.activeTab, true);
            return;
        }

        await this.init();
    }

    setActiveTab(tab: string): void {
        this.setTabState(tab);

        this.utils.logger.info(`dashboard activeItem: `, this.activeItem);
        this.utils.logger.info(`dashboard activeTab: `, this.activeTab);

        void this.navigateToSection(this.activeTab);

        if (this.activeTab === 'dashboard') {
            void this.loadOverview();
        }
    }

    public async onChartWrapperEvent(event: any) {
        this.utils.logger.info(`OverviewComponent.onChartWrapperEvent() ${JSON.stringify(event)}`);
        switch (event?.payload?.type) {
            case 'paidKeywords':
                await this.updateChartByDateRange(event?.payload?.dateRange?.value, this.keywordsChart);
                break;
            case 'organicKeywords':
                await this.updateChartByDateRange(event?.payload?.dateRange?.value, this.trafficChart);
                break;
            default:
                break;
        }

    }

    public async exportSeoComprehensivePdf(): Promise<void> {
        if (this.isExportingSeoPdf) {
            return;
        }

        this.isExportingSeoPdf = true;
        try {
            await this.seoComprehensivePdfExportService.exportPdf();
        } finally {
            this.isExportingSeoPdf = false;
        }
    }

    private async init(): Promise<boolean> {

        this.chartColors1 = this.utils.transparentChartColors;
        this.chartColors2 = this.utils.chartColors;

        if (!this.cache.currentProject) {
            this.utils.logger.info(`OverviewComponent.init() - No project selected, redirecting to manage projects`);
            this.utils.errorMessage(MessageType.ALERT, 'No project selected, redirecting to manage projects');
            this.router.navigate(['/settings/manage/projects']);
            return false;
        }

        await this.cache.getClaimsAndUpdateMenu();

        if (this.activeTab !== 'dashboard') {
            return true;
        }

        return this.loadOverview();
    }

    private loadOverview(): Promise<boolean> {
        if (this.overviewLoadPromise) {
            return this.overviewLoadPromise;
        }

        this.overviewLoadPromise = this.loadOverviewData();
        return this.overviewLoadPromise;
    }

    private async loadOverviewData(): Promise<boolean> {
        this.metricTypes = [
            { name: 'Organic', value: 'organic' },
            { name: 'Paid', value: 'paid' },
        ];

        const loaded: boolean = await this.getDashboardOverview();
        if (!loaded) {
            return false;
        }

        this.setupRankChart();
        this.getReferringLinkTypes();
        this.caclulateBacklinksAndDomains();
        this.setBackLinksNegativePositiveChart();
        this.setReferringDomainsNegativePositiveChart();
        this.getGASummaryData();
        this.metricType = this.metricTypes[0];

        return true;
    }

    private async getDashboardOverview(): Promise<boolean> {
        const dateRange = this.utils.getStableHistoricalRange(DateRangeOptions.Last12Months);
        const payload: SeoDashboardRequest = {
            target: this.utils.stripHost(this.cache.currentProject?.domain),
            locationCode: this.cache?.selectedProject?.locationCode || 2840,
            languageCode: this.cache?.selectedProject?.languageCode || 'en',
            dateFrom: dateRange.dateFrom,
            dateTo: dateRange.dateTo,
            backlinksInternalListLimit: this.cache?.seoLimits?.dashboardBacklinksInternal,
            includeSubdomains: true
        };

        this.cache.loading = true;
        try {
            const { data, error } = await this.seoApiService.getDashboardOverview(payload, true);
            if (error || !data) {
                this.utils.handleApiError(error, 'Failed to fetch dashboard overview');
                return false;
            }

            this.applyDashboardOverview(data);
            return true;
        } finally {
            this.cache.loading = false;
        }
    }

    private applyDashboardOverview(response: SeoDashboardResponse): void {
        this.dashboardGeneratedAt = response.generatedAt;
        this.dashboardSummary = response.summary;
        this.dashboardWidgets = {
            historicalRank: response.historicalRank,
            domainRank: response.domainRank,
            backlinksSummary: response.backlinksSummary,
            backlinksHistory: response.backlinksHistory
        };
        this.trafficData = response.historicalRank.data;
        this.rankedKeywordsData = response.domainRank.data?.items?.[0] ?? response.domainRank.data;
        this.backlinksSummaryData = response.backlinksSummary.data;
        this.backlinksHistoryData = response.backlinksHistory.data;
    }

    private applyGscSignal(summary: GscSummary): void {
        const clicks = summary.clicks;
        const isMaterial: boolean = Math.abs(clicks.delta) >= 10 && Math.abs(clicks.deltaPercent) >= 10;
        if (!isMaterial || !this.dashboardSummary) {
            return;
        }

        const gscStatus: 'improving' | 'declining' = clicks.delta > 0 ? 'improving' : 'declining';
        const existingStatus: string = this.dashboardSummary.status;
        const conflicts: boolean =
            (existingStatus === 'improving' && gscStatus === 'declining') ||
            (existingStatus === 'declining' && gscStatus === 'improving');
        this.dashboardSummary.status = conflicts ? 'mixed' : gscStatus;
        this.dashboardSummary.headline = conflicts
            ? 'First-party and estimated organic search signals are mixed.'
            : `Google Search clicks are ${gscStatus}.`;
        this.dashboardSummary.evidence = [
            `Search Console clicks ${clicks.delta > 0 ? 'increased' : 'decreased'} ${Math.abs(clicks.deltaPercent).toFixed(1)}% versus the prior 28 days.`,
            ...this.dashboardSummary.evidence
        ].slice(0, 3);
        this.dashboardSummary.confidence = conflicts ? 'low' : 'medium';
        this.dashboardSummary.confidenceReasons = [
            'Search Console provides first-party search performance data.',
            ...this.dashboardSummary.confidenceReasons
        ];

        if (summary.impressions.delta > 0 && summary.ctr.delta < 0) {
            this.dashboardSummary.recommendedAction =
                'Review high-impression queries with declining click-through rate and improve their search-result titles and descriptions.';
        }
    }

    public async onkeywordTrafficChange(event: any) {
        this.utils.logger.info(`OverviewComponent.onkeywordTrafficChange()`);
        this.keywordChartWrapper.selectedDateRange = {
            name: 'Last 12 Months',
            value: 'last_12_months'
        }

        this.trafficChartWrapper.selectedDateRange = {
            name: 'Last 12 Months',
            value: 'last_12_months'
        }

        //this.trafficData.items.reverse();
        const updated: boolean = await this.getHistoricalRankOverview(DateRangeOptions.Last12Months);
        if (updated) {
            this.keywordsChart.refresh();
            this.trafficChart.refresh();
        }
    }

    public onGscStateChange(state: GscDashboardState): void {
        this.gscConnected = state.connected;
        if (!state.connected || state.error || !state.summary || !this.dashboardSummary) {
            return;
        }

        this.applyGscSignal(state.summary);
    }

    private async getHistoricalRankOverview(range: DateRangeOptions): Promise<boolean> {

        const requestSequence: number = ++this.historicalRankRequestSequence;

        const dateRange = this.utils.getStableHistoricalRange(range);
        const historicalRankOverviewRequest: HistoricalRankOverviewRequest = {
            target: this.utils.stripHost(this.cache.currentProject?.domain),
            locationCode: this.cache?.selectedProject?.locationCode ? this.cache?.selectedProject?.locationCode : 2840,
            languageCode: this.cache?.selectedProject?.languageCode ? this.cache?.selectedProject?.languageCode : 'en',
            dateFrom: dateRange.dateFrom,
            dateTo: dateRange.dateTo
        }

        this.cache.loading = true;
        const { data, error } = await this.seoApiService.getHistoricalRankOverview(historicalRankOverviewRequest, true);
        this.cache.loading = false;

        if (error) {
            this.utils.handleApiError(error, 'Failed to fetch hisorical overview');
            return false;
        }

        //data.items = JSON.parse(JSON.stringify(data.items.reverse()));
        if (requestSequence !== this.historicalRankRequestSequence) {
            return false;
        }

        this.trafficData = data;
        return true;
    }

    private async getDomainRankOverview(): Promise<boolean> {

        const domainRankOverviewRequest = {
            target: this.utils.stripHost(this.cache.currentProject?.domain),
            locationCode: this.cache?.selectedProject?.locationCode ? this.cache?.selectedProject?.locationCode : 2840,
            languageCode: this.cache?.selectedProject?.languageCode ? this.cache?.selectedProject?.languageCode : 'en'
        }

        this.cache.loading = true;
        const { data, error } = await this.seoApiService.getDomainRankings(domainRankOverviewRequest, true);
        this.cache.loading = false;

        if (error) {
            this.utils.handleApiError(error, 'Failed to fetch domain rank overview');
            return false;
        }

        this.rankedKeywordsData = data?.items?.[0] ?? data;

        return true;
    }

    private async getBacklinksSummary(): Promise<boolean> {

        const backlinksRequest: BacklinksRequest = {
            target: this.utils.stripHost(this.cache.currentProject?.domain),
            internalListLimit: this.cache?.seoLimits?.dashboardBacklinksInternal,
            includeSubdomains: true,
            backlinksStatusType: 'all'
        }

        this.cache.loading = true;
        const { data, error } = await this.seoApiService.getBacklinksSummary(backlinksRequest, true);
        this.cache.loading = false;

        if (error) {
            this.utils.handleApiError(error, 'Failed to fetch backlinks summary');
            return false;
        }

        this.backlinksSummaryData = data;

        return true;
    }

    private async getBacklinksHistory(range: DateRangeOptions): Promise<boolean> {

        const dateRange = this.utils.getStableHistoricalRange(range);
        const payload: BacklinksHistoryRequest = {
            target: this.utils.stripHostAndWWW(this.cache.currentProject?.domain),
            dateFrom: dateRange.dateFrom,
            dateTo: dateRange.dateTo
        }

        this.cache.loading = true;
        const { data, error } = await this.seoApiService.getBacklinksHistory(payload, true);
        this.cache.loading = false;

        if (error) {
            this.utils.handleApiError(error, 'Failed to fetch backlinks history');
            return false;
        }

        // data.items = data.items.reverse();
        this.backlinksHistoryData = data;


        this.utils.logger.info(`DashboardComponent.getBacklinksHistory() - backlinksHistoryData: `, this.backlinksHistoryData);
        return true;
    }

    private setupRankChart(): void {

        this.rankDoughnut = {
            labels: [`Rank ${this.backlinksSummaryData?.rank}`],
            datasets: [
                {
                    data: [this.backlinksSummaryData?.rank, 1000 - this.backlinksSummaryData?.rank],
                    backgroundColor: [
                        "#FF6384",
                        "#36A2EB",
                        "#FFCE56"
                    ],
                    hoverBackgroundColor: [
                        "#FF6384",
                        "#36A2EB",
                        "#FFCE56"
                    ]
                }
            ]
        };

    }

    private getReferringLinkTypes(): void {

        const labels = [];
        const data = [];
        const referringLinkTypeKeys = this.backlinksSummaryData ? Object.keys(this.backlinksSummaryData?.referringLinksTypes) : [];
        const referringLinkTypes = this.backlinksSummaryData?.referringLinksTypes;

        referringLinkTypeKeys.forEach(key => {
            const value = !referringLinkTypes[key] ? 0 : referringLinkTypes[key];
            labels.push(`${key} ${value}`);
            data.push(value);
        });

        this.referringLinkTypeData = {
            labels: labels,
            datasets: [
                {
                    data: data,
                    backgroundColor: [
                        "#42A5F5",
                        "#66BB6A",
                        "#FFA726"
                    ],
                    hoverBackgroundColor: [
                        "#64B5F6",
                        "#81C784",
                        "#FFB74D"
                    ]
                }
            ]
        };

    }

    private caclulateBacklinksAndDomains(): void {
        this.newBacklinks = 0;
        this.newReferringDomains = 0;
        this.lostBacklinks = 0;
        this.lostReferringDomains = 0;
        this.brokenBacklinks = 0;
        this.brokenPages = 0;

        this.backlinksHistoryData?.items.forEach(item => {
            this.newBacklinks += item.newBacklinks;
            this.newReferringDomains += item.newReferringDomains;
            this.lostBacklinks += item.lostBacklinks;
            this.lostReferringDomains += item.lostReferringDomains;
            this.brokenBacklinks += item.brokenBacklinks;
            this.brokenPages += item.brokenPages;
        });
    }

    private async updateChartByDateRange(range: string, chart: PositionDistributionChartComponent): Promise<void> {
        let selectedRange: DateRangeOptions;
        switch (range) {
            case 'last_6_months':
                selectedRange = DateRangeOptions.Last6Months;
                break;
            case 'last_12_months':
                selectedRange = DateRangeOptions.Last12Months;
                break;
            case 'last_24_months':
                selectedRange = DateRangeOptions.Last24Months;
                break;
            case 'last_36_months':
                selectedRange = DateRangeOptions.Last36Months;
                break;
            case 'last_48_months':
                selectedRange = DateRangeOptions.Last48Months;
                break;
            case 'all_time':
                selectedRange = DateRangeOptions.AllTime;
                break;
            default:
                selectedRange = DateRangeOptions.Last12Months;
                break;
        }

        const updated: boolean = await this.getHistoricalRankOverview(selectedRange);
        if (updated) {
            chart.refresh();
        }
    }

    private setTabState(tab: string): void {
        const matchingItem: MenuItem | undefined = this.items?.find(item => item.tab === tab);
        this.activeTab = matchingItem ? tab : 'dashboard';
        this.activeItem = matchingItem || this.items?.find(item => item.tab === 'dashboard');
    }

    private navigateToSection(tab: string, replaceUrl: boolean = false): Promise<boolean> {
        const pathByTab: Record<string, string> = {
            dashboard: 'overview',
            'domain-research': 'domain-research',
            'ga-kpi': 'analytics',
            opportunities: 'opportunities'
        };
        return this.router.navigate(['/dashboard', pathByTab[tab] || 'overview'], {
            replaceUrl,
            queryParams: { tab: null },
            queryParamsHandling: 'merge'
        });
    }

    get currentProject() {
        return this.cache?.currentProject;
    }

    public get dashboardErrorWidgets(): string[] {
        return Object.entries(this.dashboardWidgets)
            .filter(([, widget]) => widget.status === 'error')
            .map(([name]) => name);
    }

    public get dashboardCachedWidgetCount(): number {
        return Object.values(this.dashboardWidgets).filter(widget => widget.cached).length;
    }

    public get dashboardMarketLabel(): string {
        const locationCode: number = this.cache?.selectedProject?.locationCode || 2840;
        const languageCode: string = this.cache?.selectedProject?.languageCode || 'en';
        return `${locationCode} · ${languageCode}`;
    }

    private setBackLinksNegativePositiveChart(): void {
        const labels: string[] = [];
        const newBacklinks_: number[] = [];
        const lostBacklinks_: number[] = [];

        const backLinksData = this.backlinksHistoryData ? this.backlinksHistoryData : [];

        backLinksData?.items.forEach(i => {
            newBacklinks_.push(Math.abs(i.newBacklinks));
            lostBacklinks_.push(-Math.abs(i.lostBacklinks));
            const [datePart] = i.date.split(' ');
            const [year, month] = datePart.split('-');
            const label = `${year}-${month}-${'01'}`;
            labels.push(this.utils.formatDate(label, 'MMM YYYY'));
        });
        this.utils.logger.info(`DashboardComponent.setBackLinksNegativePositiveChart() - labels: ${labels}`);

        this.backLinksPositiveNegativeChartData = {
            labels: labels,
            datasets: [
                {
                    label: 'New Back links',
                    backgroundColor: '#873EFE',
                    data: newBacklinks_,
                    stack: 'combined',

                },
                {
                    label: 'Lost Back Links',
                    backgroundColor: '#44486d',
                    data: lostBacklinks_,
                    stack: 'combined',

                }
            ]
        };

        this.backLinksPositiveNegativeChartOptions = {
            indexAxis: 'x',
            maintainAspectRatio: false,
            aspectRatio: 0.8,
            responsive: true,
            layout: {
                padding: {
                    top: 10
                }
            },
              plugins: {
                legend: {
                  labels: {
                    usePointStyle: true
                      },
                      onHover: (event, legendItem) => {
                          const canvas = event.native.target;
                          canvas.style.cursor = 'pointer';
                        }
                }
              },
            scales: {
                x: {
                    stacked: true,
                    barPercentage: 2
                },
                y: {
                    stacked: true,
                    beginAtZero: true,
                    ticks: {
                        font: {
                            weight: 500
                        }
                    },
                    grid: {
                        drawBorder: false
                    }
                }
            }
        };
    }
    private setReferringDomainsNegativePositiveChart(): void {
        //console.log(`DashboardComponent.setReferringDomainsNegativePositiveChart()`);
        const labels: string[] = [];
        const newBacklinks_: number[] = [];
        const lostBacklinks_: number[] = [];

        const backLinksData = this.backlinksHistoryData ? this.backlinksHistoryData : [];
        backLinksData?.items.forEach(i => {
            newBacklinks_.push(Math.abs(i.newReferringDomains));
            lostBacklinks_.push(-Math.abs(i.lostReferringDomains));
            const [datePart] = i.date.split(' ');
            const [year, month] = datePart.split('-');
            const label = `${year}-${month}-${'01'}`;
            labels.push(this.utils.formatDate(label, 'MMM YYYY'));
        });
        //console.log(`DashboardComponent.setReferringDomainsNegativePositiveChart() - labels: ${labels}`);
        this.referringDomainsPositiveNegativeChartData = {
            labels: labels,
            datasets: [
                {
                    label: 'New Referring Domains',
                    backgroundColor: '#873EFE',
                    data: newBacklinks_,


                },
                {
                    label: 'Lost Referring Domains',
                    backgroundColor: '#44486d',
                    data: lostBacklinks_,


                }
            ]
        };

        this.referringDomainsPositiveNegativeChartOptions = {
            indexAxis: 'x',
            maintainAspectRatio: false,
            aspectRatio: 0.8,
            responsive: true,
            layout: {
                padding: {
                    top: 10
                }
            },
              plugins: {
                legend: {
                  labels: {
                    usePointStyle: true
                      },
                      onHover: (event, legendItem) => {
                          const canvas = event.native.target;
                          canvas.style.cursor = 'pointer';
                        }
                }
              },
            scales: {
                x: {
                    stacked: true,
                    barPercentage: 2
                },
                y: {
                    stacked: true,
                    beginAtZero: true,
                    ticks: {
                        callback: function (value: any) {
                            return Math.abs(value);
                        },
                        font: {
                            weight: 500
                        }
                    },
                    grid: {
                        drawBorder: false
                    }
                }
            }
        };
    }

    private async getGASummaryData(): Promise<void> {
        this.utils.logger.info(`DashboardComponent.getGASummaryData()`);
        let totalActiveUsers = 0;
        let totalSessions = 0;
        let totalNewUsers = 0;

        if (!this.cache?.selectedProject?.externalProviders?.google?.propertyId) {
            this.gaConnection = false;
            return;
        }

        const google = this.cache.selectedProject.externalProviders.google;
        if (!(google?.accessToken || google?.refreshToken)) {
            this.gaAuthorizationRequired = true;
            this.gaConnection = false;
            return;
        }

        try {
            const data: any = await this.onGetGoogleAnalyticsData();
            if (!data || !Array.isArray(data.rows)) {
                this.gaConnection = false;
                return;
            }
            this.utils.logger.info('Google Analytics Data dashboard:', data);
            this.gaSummaryTableData = data.rows.map(row => {
                if (!Array.isArray(row.dimensionValues) || !Array.isArray(row.metricValues)) {
                    return null;
                }

                const [country, city] = row.dimensionValues.map((dv: any) => dv.value || '(not set)');
                const [sessions, activeUsers, newUsers] = row.metricValues.map((mv: any) => {
                    const value = parseInt(mv.value, 10);
                    return isNaN(value) ? 0 : value;
                });

                // Update totals
                totalSessions += sessions;
                totalActiveUsers += activeUsers;
                totalNewUsers += newUsers;

                return {
                    country,
                    city,
                    sessions,
                    activeUsers,
                    newUsers
                };
            }).filter(row => row !== null);
            this.gaConnection = true;
            this.gaAuthorizationRequired = false;

            // Set the summary data
            this.gaSummaryDataActiveUser = totalActiveUsers;
            this.gaSummaryDataSessions = totalSessions;
            this.gaSummaryDataNewUser = totalNewUsers;

            // Array of total summary
            this.gaSummaryTotalArray = {
                totalSessions,
                totalActiveUsers,
                totalNewUsers
            };

        } catch (error) {
            this.gaAuthorizationRequired = isGoogleAuthorizationError(error);
            if (this.gaAuthorizationRequired) {
                google.accessToken = '';
                google.refreshToken = '';
            } else {
                this.utils.handleApiError(error, 'Error processing google analytics data');
            }
            this.gaConnection = false;
        }
    }

    public async onGetGoogleAnalyticsData(): Promise<unknown> {
        const params = {
            accessToken: this.cache?.selectedProject?.externalProviders?.google?.accessToken,
            userId: this.cache.userId,
            propertyId: this.cache?.selectedProject?.externalProviders?.google?.propertyId,
            projectId: this.cache?.selectedProject?.id,
            reportData: {
                "dimensions": [
                    { "name": "country" },
                    { "name": "city" }
                ],
                "metrics": [
                    {
                        "name": "sessions"
                    },
                    {
                        "name": "activeUsers"
                    },
                    {
                        "name": "newUsers"
                    }
                ],
                "dateRanges": [
                    { "startDate": "30daysAgo", "endDate": "today" }
                ]
            }
        };

        const { data, error } = await this.cache.getGoogleAnalyticsData(params);

        if (error) {
            throw error;
        }

        return data;
    }
}


